/**
 * The Finance Overview (WALLIYO-0132), built to the approved design.
 *
 * Money as money, not as a row count. "61 payments" says nothing an accountant
 * can use; what came in, what is owed and what is waiting on a signature do.
 *
 * The queue is the department's real job: somebody asked to go above their own
 * limit and cannot move until a person answers. Each row is titled by the action
 * they asked to take, using the name the Actions screen already gives it, so the
 * two screens never disagree about what a thing is called.
 */

import type { FinanceOverview as Data, QueueRow } from "@wallyo/contracts/console/overview";

import { actionName, t, tCount, type MessageId } from "../../i18n";
import { formatDate, formatNumber } from "../../i18n/format";
import { Figures } from "./OverviewScreen";
import { Tallies } from "./QueueRows";

export function FinanceOverviewView({ data }: { data: Data }) {
  return (
    <>
      <Figures figures={data.figures} />

      <div className="ov-split">
        <section className="card">
          <div className="ov-card-head">
            <h2>{t("overview.finance.aboveLimit")}</h2>
            <span className="foot">{t("overview.finance.theyAskedYouDecide")}</span>
          </div>
          {data.approvals.length === 0 ? (
            <p className="foot">{t("overview.finance.nothingWaitingHere")}</p>
          ) : (
            data.approvals.map((row, index) => <Approval key={`${row.rowId}-${index}`} row={row} />)
          )}
        </section>

        <div className="ov-side">
          <section className="card">
            <h2>{t("overview.finance.subscriptions")}</h2>
            <Tallies tallies={data.subscriptions} area="finance" />
          </section>

          {data.promo ? (
            <section className="card">
              <h2>{t("overview.finance.promosRunning")}</h2>
              <p className="foot">
                {data.promo.endsOn
                  ? t("overview.finance.promoEnds", {
                      name: data.promo.name,
                      on: formatDate(data.promo.endsOn, { day: "numeric", month: "long" }),
                    })
                  : t("overview.finance.promoNoEnd", { name: data.promo.name })}
              </p>
              {/* Both halves or neither: what it cost alone is true of every
                  promotion ever run and says nothing about this one. */}
              <p className="foot">
                {t("overview.finance.promoCost", { given: data.promo.given, earned: data.promo.earned })}
              </p>
            </section>
          ) : null}
        </div>
      </div>
    </>
  );
}

/** What they asked to do, how much of it, and how long they have been stuck. */
function Approval({ row }: { row: QueueRow }) {
  return (
    <div className="ov-row">
      <span className={`ov-bar-side ${row.tone}`} aria-hidden="true" />
      <span className="grow">
        <span className="ov-row-what">
          {row.amount ? `${actionName(row.rowId)} ${row.amount}` : actionName(row.rowId)}
        </span>
        <span
          className={
            row.tone === "bad" ? "ov-row-why danger" : row.tone === "warn" ? "ov-row-why warn-text" : "ov-row-why"
          }
        >
          {row.subject} ·{" "}
          {row.ceiling ? t("overview.finance.theirLimit", { ceiling: row.ceiling }) : t("overview.finance.noLimit")} ·{" "}
          {row.days === 0
            ? t("overview.finance.today")
            : tCount("overview.finance.waiting", row.days ?? 0, { days: formatNumber(row.days ?? 0) })}
        </span>
      </span>
      <a className={row.tone === "bad" ? "btn primary" : "btn"} href={row.href}>
        {t(`overview.action.${row.actionId}` as MessageId)}
      </a>
    </div>
  );
}
